import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { productCustomizationApi } from "../../services/productCustomizationApi";
import { DesignPreviewThumbnail } from "./DesignPreviewThumbnail";
import { getImageUrl } from "../../utils/imageUtils";
import { useApi } from "../../context/ApiContext";
import { FaCheck } from "react-icons/fa";

interface DesignSelection {
  customizationId: string;
  quantity: number;
}

interface DesignSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  customizationType: "2d-print" | "3d-print";
  onConfirm: (selections: DesignSelection[]) => void;
}

export const DesignSelectionModal: React.FC<DesignSelectionModalProps> = ({
  isOpen,
  onClose,
  productId,
  customizationType,
  onConfirm,
}) => {
  const { api } = useApi();
  const [designs, setDesigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selections, setSelections] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (isOpen && productId) {
      fetchDesigns();
    } else {
      // Reset selections when modal closes
      setSelections(new Map());
    }
  }, [isOpen, productId]);

  const fetchDesigns = async () => {
    try {
      setLoading(true);
      const fetchedDesigns = await productCustomizationApi.getCustomizations({
        productId,
        customizationType,
      });
      // Filter to only show draft designs
      const drafts = fetchedDesigns.filter((d: any) => d.isDraft);
      setDesigns(drafts);
    } catch (error) {
      console.error("Error fetching designs:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDesign = (customizationId: string) => {
    const newSelections = new Map(selections);
    if (newSelections.has(customizationId)) {
      newSelections.delete(customizationId);
    } else {
      newSelections.set(customizationId, 1);
    }
    setSelections(newSelections);
  };

  const updateQuantity = (customizationId: string, quantity: number) => {
    if (quantity < 1) return;
    const newSelections = new Map(selections);
    newSelections.set(customizationId, quantity);
    setSelections(newSelections);
  };

  const handleConfirm = () => {
    const selectionArray: DesignSelection[] = Array.from(selections.entries()).map(
      ([customizationId, quantity]) => ({
        customizationId,
        quantity,
      })
    );
    onConfirm(selectionArray);
    onClose();
  };

  const selectedCount = selections.size;
  const totalQuantity = useMemo(() => {
    return Array.from(selections.values()).reduce((sum, qty) => sum + qty, 0);
  }, [selections]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Designs to Add to Cart"
      size="xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-sm">
            {selectedCount > 0 ? (
              <div className="text-green-700 font-medium">
                ✓ {selectedCount} design{selectedCount !== 1 ? "s" : ""} selected • Total quantity: {totalQuantity}
              </div>
            ) : (
              <div className="text-gray-500 italic">
                Select designs above to continue
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedCount === 0}
              className={`transition-all ${
                selectedCount > 0
                  ? "bg-blue-600 hover:bg-blue-700 shadow-lg transform hover:scale-105"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              {selectedCount > 0 ? (
                <>Add to Cart ({totalQuantity})</>
              ) : (
                "Select Designs First"
              )}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Instructions */}
        {!loading && designs.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>How to proceed:</strong> Click on the designs you want to add to your cart. Adjust the quantity for each selected design, then click the <strong>"Add to Cart"</strong> button below to continue.
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <p className="text-gray-500">Loading designs...</p>
          </div>
        ) : designs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No saved designs found. Create a design first.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto pr-2 pb-4">
            {designs.map((design) => {
              const isSelected = selections.has(design._id);
              const quantity = selections.get(design._id) || 1;

              return (
                <div
                  key={design._id}
                  className={`border-2 rounded-lg overflow-hidden transition-all ${
                    isSelected
                      ? "border-blue-500 shadow-lg ring-2 ring-blue-200"
                      : "border-gray-200 hover:border-gray-400 hover:shadow-md"
                  }`}
                >
                  <div
                    className="aspect-square bg-gray-100 flex items-center justify-center cursor-pointer relative overflow-hidden group"
                    onClick={() => toggleDesign(design._id)}
                    title={isSelected ? "Click to deselect" : "Click to select"}
                  >
                    {/* Use generated PNG image if available */}
                    {((customizationType === "2d-print" && design.previewImage) || 
                      (customizationType === "3d-print" && (design.previewImage || design.modelPreviewImage))) ? (
                      <img
                        src={getImageUrl(
                          customizationType === "2d-print" 
                            ? design.previewImage 
                            : (design.previewImage || design.modelPreviewImage),
                          api.baseURL
                        )}
                        alt="Design preview"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          // Hide image on error and show fallback
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : customizationType === "2d-print" && design.elements && Array.isArray(design.elements) && design.elements.length > 0 ? (
                      /* Fallback to client-side rendering for 2D designs without preview image */
                      <DesignPreviewThumbnail
                        elements={design.elements}
                        backgroundColor={design.backgroundColor || "#FFFFFF"}
                        backgroundImage={design.backgroundImage}
                        canvasSize={design.canvasSize || "mug"}
                        size={160}
                      />
                    ) : (
                      /* Fallback for designs without preview or elements */
                      <div className="text-center p-4">
                        {customizationType === "2d-print" ? (
                          <>
                            <p className="text-sm text-gray-600 mb-2">
                              {design.elements?.length || 0} element{design.elements?.length !== 1 ? "s" : ""}
                            </p>
                            <p className="text-xs text-gray-400">
                              {design.canvasSize || "Custom"} canvas
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-gray-600">
                            3D Design
                          </p>
                        )}
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute top-2 right-2 bg-blue-500 text-white rounded-full p-1.5 z-10">
                        <FaCheck className="text-xs" />
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-700 flex-shrink-0">
                          Quantity:
                        </label>
                        <div className="flex items-center gap-1 flex-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(design._id, Math.max(1, quantity - 1));
                            }}
                            className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100"
                          >
                            -
                          </button>
                          <Input
                            type="number"
                            min="1"
                            value={quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (!isNaN(val) && val > 0) {
                                updateQuantity(design._id, val);
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-16 h-6 text-center text-sm px-1"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(design._id, quantity + 1);
                            }}
                            className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
};

